import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  PATHS,
  CatalogHeaders,
  CatalogDummyData,
} from 'src/common/constants';
import Actions from '../Actions';
import Button from '../Button';
import Empty from '../Empty';
import FontAwesomeIcon from '../Icon';
import ItemForm from '../ItemForm';
import Table, { Hidden } from '../Table';
import LoadState from 'src/components/LoadState/LoadState';
import Skeleton from 'src/components/Skeleton/Skeleton';
import {
  setItemFormData,
  clearItemFormData,
} from 'src/store/actions/itemForm';
import { selectSearch } from 'src/store/selectors/search';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { CSVLink } from 'react-csv';
import { fetchItems } from 'src/store/actions/items';
import CatalogueImportDialogue from 'src/pages/Catalog/Components/CatalogueImportDialogue';
import Search from 'src/components/Search/Search';
import { useTablePagination } from 'src/SharedCustomHooks/UseTablePagination/useTablePagination';

const SKELETON_ROW_COUNT: number = 13;

interface ItemsTableProps {
  items: any[];
  isLoadingItems?: boolean;
  onSaveItem: (item: any) => void;
  onDeleteItem: (data: any) => void;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ItemsTable = ({
  items,
  isLoadingItems = false,
  onSaveItem,
  onDeleteItem,
  onSearch,
}: ItemsTableProps) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showImportDialogue, setShowImportDialogue] = useState(false);

  const { itemsPerPage, onItemsPerPageChange } = useTablePagination();

  const dispatch = useDispatch();

  const user = useSelector(selectAuthenticatedUser);
  const search = useSelector(selectSearch);
  const loader = useSelector(selectFormLoader);

  const handleImportComplete = () => {
    setShowImportDialogue(false);
    dispatch(fetchItems());
  };

  const isLoading: boolean = isLoadingItems || loader;
  const isEmpty: boolean = !isLoading && items.length === 0;

  const tableColumns: any[] = [
    {
      label: 'Item',
      sortable: true,
      accessor: 'name',
      cell: (data) => (
        <Link
          to={{
            pathname: PATHS.ITEMS_VIEW.replace(':id', data.id),
            state: { item: data },
          }}
        >
          {data.name}
        </Link>
      ),
    },
    {
      label: 'Make',
      cell: (data) => (data && data.makeName ? data.makeName : '—'),
    },
    {
      label: 'Model',
      cell: (data) => (data && data.model ? data.model : '—'),
    },
    {
      xsHide: true,
      label: 'Type',
      accessor: 'typeName',
    },
    {
      center: true,
      xsHide: true,
      label: 'Warranty Plan',
      cell: (data) =>
        data.warrantyYears === '0'
          ? 'NA'
          : `${data.warrantyYears} year${data.warrantyYears > 1 ? 's' : ''}`,
    },
    {
      center: true,
      xsHide: true,
      label: 'Property',
      cell: (data) => (
        <div
          className="title_elipse"
          title={
            data.properties &&
            data.properties.length > 0 &&
            data.properties.map((ps, index) => {
              return (
                ps.name +
                (data.properties.length - 1 === index ? '' : ', ')
              );
            })
          }
        >
          {data.properties &&
            data.properties.length > 0 &&
            data.properties.map((ps, index) => {
              return (
                ps.name +
                (data.properties.length - 1 === index ? '' : ', ')
              );
            })}
        </div>
      ),
    },
    {
      cell: (data) =>
        user.roles[0].role.name === 'Strata Manager' ? null : (
          <Hidden>
            <Button
              color="semantic"
              label={<FontAwesomeIcon glyph="pencil" weight="light" />}
              onClick={() => {
                setShowOverlay(true);
                dispatch(
                  setItemFormData({
                    ...data,
                    selectedProperties: data.properties.map((pty) => ({
                      value: pty.id,
                      label: pty.name,
                    })),
                  })
                );
              }}
            />
            <Button
              color="semantic"
              onClick={() =>
                window.confirm(
                  `Are you sure you want to delete ${data.name}?`
                ) && onDeleteItem(data)
              }
              label={<FontAwesomeIcon glyph="trash" weight="light" />}
            />
          </Hidden>
        ),
    },
  ];

  const skeletonColumns: any[] = tableColumns.map((c: any) => ({
    ...c,
    cell: () => <Skeleton variant="text" width="70%" />,
  }));

  const skeletonData: any[] = Array.from(
    { length: SKELETON_ROW_COUNT },
    (_: unknown, i: number) => ({ id: `items-skeleton-${i}` })
  );

  const emptyNode: React.ReactNode = (
    <Empty
      direction="ttb"
      headline="Add items to your catalog"
      strapline="Keep all your contacts you here so it's easier for you to manage."
      searchIsActive={search && search.items ? search.items : ''}
      cta={
        user.roles[0].role.name === 'Strata Manager' ? null : (
          <Button
            color="teal"
            label="Add Item"
            onClick={() => {
              dispatch(
                setItemFormData({
                  name: '',
                  model: '',
                  makeName: '',
                  typeName: '',
                  warrantyYears: '',
                })
              );
              setShowOverlay(true);
            }}
            icon={
              <FontAwesomeIcon glyph="boxes" weight="light" size="small" />
            }
            size="small"
          />
        )
      }
      image={
        <img
          alt="Add items to your catalog"
          src="/assets/empty/items.svg"
        />
      }
    />
  );

  return (
    <>
      <Actions compact>
        <Search
          size="small"
          placeholder="Search Items..."
          onChange={onSearch}
          theme={undefined}
        />
        {user.roles[0].role.name === 'Strata Manager' ? null : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}
          >
            <Button
              color="teal"
              label="Add Item"
              onClick={() => {
                dispatch(
                  setItemFormData({
                    name: '',
                    model: '',
                    makeName: '',
                    typeName: '',
                    warrantyYears: '',
                  })
                );
                setShowOverlay(true);
              }}
              icon={
                <FontAwesomeIcon
                  glyph="boxes"
                  weight="light"
                  size="small"
                />
              }
              size="small"
            />
            <Button
              color="teal"
              label="Bulk Items Import"
              onClick={() => setShowImportDialogue(true)}
              size="small"
            />
            <div title="Download CSV template" style={{ display: 'flex' }}>
              <CSVLink
                data={CatalogDummyData}
                headers={CatalogHeaders}
                filename="Items.csv"
                target="_blank"
              >
                <FontAwesomeIcon
                  glyph="download"
                  weight="regular"
                  size="big"
                />
              </CSVLink>
            </div>
          </div>
        )}
      </Actions>
      <LoadState
        isLoading={false}
        isEmpty={isEmpty}
        emptyNode={emptyNode}
      >
        <Table
          columns={isLoading ? skeletonColumns : tableColumns}
          data={isLoading ? skeletonData : items}
          paginate={{ itemsPerPage, position: 'both' }}
          paginationSkeleton={isLoading}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      </LoadState>
      {showOverlay && (
        <ItemForm
          onClose={() => {
            setShowOverlay(false);
            dispatch(clearItemFormData());
          }}
          onSaveItem={onSaveItem}
        />
      )}
      <CatalogueImportDialogue
        isOpen={showImportDialogue}
        onClose={() => setShowImportDialogue(false)}
        onImportComplete={handleImportComplete}
      />
    </>
  );
};

export default ItemsTable;